import React from 'react';
import { IconType } from 'react-icons';
import { CircleSpinner } from 'react-spinners-kit';
import styled, { css } from 'styled-components';
import { colors } from './Styles';

export enum RhButtonMode {
  primary,
  secondary,
  hollow,
  text,
  subtle,
}

interface Props extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'title'> {
  // inputs
  /** title of the button */
  title?: React.ReactNode;
  /** button mode or type, but type is taken by native `button` element */
  mode?: RhButtonMode;
  /** suffix icon of the button */
  suffixIcon?: IconType;
  /** prefix icon of the button */
  prefixIcon?: IconType;
  /** suffix icon of the button */
  suffixSvg?: React.FC<React.SVGProps<SVGSVGElement>>;
  /** prefix icon of the button */
  prefixSvg?: React.FC<React.SVGProps<SVGSVGElement>>;
  /** size of the icons */
  iconSize?: number;
  /** if status is set to loading */
  loading?: boolean;
  /** background color */
  background?: string;
  /** override stylte of text */
  textStyle?: React.CSSProperties;
  /** color of icon */
  iconColor?: string;
}

export const RhButton = (props: Props) => {
  const {
    title,
    mode = RhButtonMode.primary,
    suffixIcon: SuffixIcon,
    prefixIcon: PrefixIcon,
    iconSize = 15,
    loading,
    background,
    textStyle,
    suffixSvg: SuffixSvg,
    prefixSvg: PrefixSvg,
    iconColor,
    ...rest
  } = props;
  return (
    <Button mode={mode} background={background} {...rest}>
      {loading ? (
        <CircleSpinner color={colorLib[mode]} size={14} />
      ) : (
        <>
          {PrefixSvg && <PrefixSvg fill={iconColor} width={iconSize} height={iconSize} />}
          {PrefixIcon && <PrefixIcon color={iconColor} size={iconSize} />}
          {typeof title === 'string' ? <Text style={textStyle}>{title}</Text> : title}
          {SuffixIcon && <SuffixIcon color={iconColor} size={iconSize} />}
          {SuffixSvg && <SuffixSvg fill={iconColor} width={iconSize} height={iconSize} />}
        </>
      )}
    </Button>
  );
};

const colorLib = {
  [RhButtonMode.primary]: '#fff',
  [RhButtonMode.secondary]: colors.primary,
  [RhButtonMode.hollow]: colors.primary,
  [RhButtonMode.text]: colors.primary,
  [RhButtonMode.subtle]: colors.grey,
};

const Button = styled.button<{ mode: RhButtonMode; background?: string }>`
  all: unset;
  outline: none;
  appearance: none;
  padding: 10px 20px;
  border-radius: 40px;
  display: flex;
  align-items: center;
  cursor: pointer;
  font-weight: bold;
  font-size: 14px;
  ${({ mode, background }) => {
    switch (mode) {
      case RhButtonMode.primary:
        return css`
          background-color: ${colors.primary};
          color: #fff;
          & > svg {
            fill: #fff;
          }
        `;
      case RhButtonMode.secondary:
        return css`
          background-color: ${colors.secondary};
          color: ${colors.primary};
          & > svg {
            fill: ${colors.primary};
          }
        `;
      case RhButtonMode.hollow:
        return css`
          background-color: #fff;
          border: 1px solid ${colors.primary};
          color: ${colors.primary};
        `;
      case RhButtonMode.text:
        return css`
          color: ${colors.primary};
          padding: 0;
        `;
      case RhButtonMode.subtle:
        return css`
          background: ${background || colors.background};
          border: 1px solid ${colors.faintGrey};
          padding: 9px;
          color: ${colors.grey};
        `;
      default:
        break;
    }
  }};

  &:disabled {
    opacity: 0.4;
  }

  & > :not(:last-child) {
    margin-right: 6px;
  }
`;

const Text = styled.span`
  white-space: nowrap;
`;
